import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Download, Printer } from 'lucide-react';
import { PageHeader, Tabs } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Combobox } from '@/components/ui/Combobox';
import { SectionSpinner } from '@/components/ui/Spinner';
import { EmptyState, TBody, TD, TH, THead, TR, Table } from '@/components/ui/Table';
import { toast } from '@/components/ui/Toast';
import { agentsApi } from '@/services/endpoints/masterData';
import { reportsApi } from '@/services/endpoints/transactions';
import { queryKeys } from '@/services/queryKeys';
import { ApiError } from '@/services/apiClient';
import { formatCents } from '@/domain/money';
import { addDays, formatDate, today } from '@/lib/dateUtils';
import { downloadBlob } from '@/lib/utils';
import { useAuthStore } from '@/app/store/authStore';
import { CollectionSheet } from '../components/CollectionSheet';

type TabId = 'agentDaily' | 'agentTotals' | 'shopBreakdown' | 'summary' | 'cheques';

export function ReportsPage(): JSX.Element {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const isAgent = user?.role === 'AGENT';

  const [tab, setTab] = useState<TabId>('agentDaily');
  const [date, setDate] = useState(today());
  const [from, setFrom] = useState(addDays(today(), -29));
  const [to, setTo] = useState(today());

  // An agent can only ever report on themselves, so their own id is fixed.
  const [agentId, setAgentId] = useState<number | null>(isAgent ? user.agentId : null);
  const [agentLabel, setAgentLabel] = useState(isAgent ? (user?.fullName ?? '') : '');
  const [isDownloading, setDownloading] = useState(false);

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'agentDaily', label: t('report.agentDaily') },
    ...(isAgent
      ? []
      : [
          { id: 'agentTotals' as const, label: t('report.agentTotals') },
          { id: 'shopBreakdown' as const, label: t('report.shopBreakdown') },
        ]),
    { id: 'summary', label: t('report.summary') },
    { id: 'cheques', label: t('report.cheques') },
  ];

  const downloadPdf = async () => {
    if (!agentId) return;
    setDownloading(true);
    try {
      const blob = await reportsApi.agentDailyPdf(agentId, date);
      downloadBlob(blob, `collection-summary-${date}.pdf`);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t('errors.unknown'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title={t('report.title')} className="no-print" />

      <div className="no-print">
        <Tabs tabs={tabs} active={tab} onChange={setTab} />
      </div>

      {tab === 'agentDaily' && (
        <>
          <Card className="no-print">
            <div className="flex flex-wrap items-end gap-3 p-4">
              {!isAgent && (
                <div className="min-w-56">
                  <Combobox
                    label={t('report.selectAgent')}
                    value={agentId}
                    initialLabel={agentLabel}
                    onChange={(id, option) => {
                      setAgentId(id);
                      setAgentLabel(option?.label ?? '');
                    }}
                    onSearch={async (term) => {
                      const results = await agentsApi.suggest(term);
                      return results.map((agent) => ({
                        id: agent.id,
                        label: agent.name,
                        sublabel: agent.code,
                      }));
                    }}
                    required
                  />
                </div>
              )}

              <Input
                label={t('report.selectDate')}
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                className="w-auto"
                required
              />

              <div className="ml-auto flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => window.print()}
                  disabled={!agentId}
                  leftIcon={<Printer className="h-4 w-4" />}
                >
                  {t('report.printReport')}
                </Button>
                <Button
                  onClick={downloadPdf}
                  isLoading={isDownloading}
                  disabled={!agentId}
                  leftIcon={<Download className="h-4 w-4" />}
                >
                  {t('report.downloadPdf')}
                </Button>
              </div>
            </div>
          </Card>

          <AgentDailyReport agentId={agentId} date={date} />
        </>
      )}

      {tab !== 'agentDaily' && (
        <Card className="no-print">
          <div className="flex flex-wrap items-end gap-3 p-4">
            {(tab === 'agentTotals' || tab === 'shopBreakdown') && (
              <Input
                label={t('common.date')}
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                className="w-auto"
              />
            )}

            {(tab === 'summary' || tab === 'cheques') && (
              <>
                <Input
                  label={t('common.from')}
                  type="date"
                  value={from}
                  onChange={(event) => setFrom(event.target.value)}
                  className="w-auto"
                />
                <Input
                  label={t('common.to')}
                  type="date"
                  value={to}
                  onChange={(event) => setTo(event.target.value)}
                  className="w-auto"
                />
              </>
            )}
          </div>
        </Card>
      )}

      {tab === 'agentTotals' && <AgentTotalsReport date={date} />}
      {tab === 'shopBreakdown' && <ShopBreakdownReport date={date} />}
      {tab === 'summary' && <SummaryReport from={from} to={to} />}
      {tab === 'cheques' && <ChequeRegisterReport from={from} to={to} />}
    </div>
  );
}

// --- Report 3: the printed sheet -------------------------------------------

function AgentDailyReport({ agentId, date }: { agentId: number | null; date: string }): JSX.Element {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.reports.agentDaily(agentId ?? 0, date),
    queryFn: () => reportsApi.agentDaily(agentId!, date),
    enabled: agentId !== null,
  });

  if (agentId === null) {
    return (
      <Card>
        <EmptyState title={t('report.selectAgent')} />
      </Card>
    );
  }

  if (isLoading) return <SectionSpinner />;
  if (!data) return <EmptyState title={t('report.noData')} />;

  return <CollectionSheet report={data} />;
}

// --- Report 1: per-agent totals --------------------------------------------

function AgentTotalsReport({ date }: { date: string }): JSX.Element {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.reports.agentTotals(date),
    queryFn: () => reportsApi.agentTotals(date),
  });

  if (isLoading) return <SectionSpinner />;

  const grand = (data ?? []).reduce(
    (sum, row) => ({
      cash: sum.cash + row.cashCents,
      cheque: sum.cheque + row.chequeCents,
      total: sum.total + row.totalCents,
    }),
    { cash: 0, cheque: 0, total: 0 },
  );

  return (
    <Card>
      <Table>
        <THead>
          <TR>
            <TH>{t('report.selectAgent')}</TH>
            <TH align="center">{t('report.columns.receipts')}</TH>
            <TH align="center">{t('report.columns.customers')}</TH>
            <TH align="right">{t('report.columns.cash')}</TH>
            <TH align="right">{t('report.columns.cheque')}</TH>
            <TH align="right">{t('report.columns.total')}</TH>
          </TR>
        </THead>
        <TBody>
          {(data ?? []).map((row) => (
            <TR key={row.agentId}>
              <TD className="font-medium text-slate-900">{row.agentName}</TD>
              <TD align="center" numeric>
                {row.receiptCount}
              </TD>
              <TD align="center" numeric>
                {row.shopCount}
              </TD>
              <TD align="right" numeric>
                {formatCents(row.cashCents)}
              </TD>
              <TD align="right" numeric>
                {formatCents(row.chequeCents)}
              </TD>
              <TD align="right" numeric className="font-semibold text-slate-900">
                {formatCents(row.totalCents)}
              </TD>
            </TR>
          ))}

          <TR className="bg-slate-50 font-semibold">
            <TD colSpan={3} className="text-right">
              {t('common.total')}
            </TD>
            <TD align="right" numeric>
              {formatCents(grand.cash)}
            </TD>
            <TD align="right" numeric>
              {formatCents(grand.cheque)}
            </TD>
            <TD align="right" numeric>
              {formatCents(grand.total)}
            </TD>
          </TR>
        </TBody>
      </Table>
    </Card>
  );
}

// --- Report 2: per-shop breakdown ------------------------------------------

function ShopBreakdownReport({ date }: { date: string }): JSX.Element {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.reports.shopBreakdown(date),
    queryFn: () => reportsApi.shopBreakdown(date),
  });

  if (isLoading) return <SectionSpinner />;
  if (!data || data.length === 0) {
    return (
      <Card>
        <EmptyState title={t('report.noData')} />
      </Card>
    );
  }

  return (
    <Card>
      <Table>
        <THead>
          <TR>
            <TH>{t('shop.clientName')}</TH>
            <TH>{t('shop.route')}</TH>
            <TH>{t('shop.agent')}</TH>
            <TH align="center">{t('report.columns.receipts')}</TH>
            <TH align="right">{t('report.columns.cash')}</TH>
            <TH align="right">{t('report.columns.cheque')}</TH>
            <TH align="right">{t('report.columns.total')}</TH>
          </TR>
        </THead>
        <TBody>
          {data.map((row) => (
            <TR key={row.shopId}>
              <TD className="font-medium text-slate-900">{row.shopName}</TD>
              <TD>{row.routeName ?? '—'}</TD>
              <TD>{row.agentName ?? '—'}</TD>
              <TD align="center" numeric>
                {row.receiptCount}
              </TD>
              <TD align="right" numeric>
                {formatCents(row.cashCents)}
              </TD>
              <TD align="right" numeric>
                {formatCents(row.chequeCents)}
              </TD>
              <TD align="right" numeric className="font-semibold text-slate-900">
                {formatCents(row.totalCents)}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </Card>
  );
}

// --- Report 4: daily summary -----------------------------------------------

function SummaryReport({ from, to }: { from: string; to: string }): JSX.Element {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.reports.summary(from, to),
    queryFn: () => reportsApi.summary(from, to),
  });

  if (isLoading) return <SectionSpinner />;
  if (!data) return <EmptyState title={t('report.noData')} />;

  return (
    <Card>
      <Table>
        <THead>
          <TR>
            <TH>{t('common.date')}</TH>
            <TH align="center">{t('report.columns.receipts')}</TH>
            <TH align="right">{t('report.columns.cash')}</TH>
            <TH align="right">{t('report.columns.cheque')}</TH>
            <TH align="right">{t('report.columns.total')}</TH>
          </TR>
        </THead>
        <TBody>
          {data.days.map((day) => (
            <TR key={day.date} className={day.totalCents === 0 ? 'text-slate-400' : undefined}>
              <TD>{formatDate(day.date)}</TD>
              <TD align="center" numeric>
                {day.receiptCount}
              </TD>
              <TD align="right" numeric>
                {formatCents(day.cashCents)}
              </TD>
              <TD align="right" numeric>
                {formatCents(day.chequeCents)}
              </TD>
              <TD align="right" numeric className="font-semibold">
                {formatCents(day.totalCents)}
              </TD>
            </TR>
          ))}

          <TR className="bg-slate-50 font-semibold">
            <TD>{t('common.total')}</TD>
            <TD align="center" numeric>
              {data.totals.receiptCount}
            </TD>
            <TD align="right" numeric>
              {formatCents(data.totals.cashCents)}
            </TD>
            <TD align="right" numeric>
              {formatCents(data.totals.chequeCents)}
            </TD>
            <TD align="right" numeric>
              {formatCents(data.totals.totalCents)}
            </TD>
          </TR>
        </TBody>
      </Table>
    </Card>
  );
}

// --- Cheque register --------------------------------------------------------

function ChequeRegisterReport({ from, to }: { from: string; to: string }): JSX.Element {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.reports.cheques(from, to),
    queryFn: () => reportsApi.cheques(from, to),
  });

  if (isLoading) return <SectionSpinner />;
  if (!data || data.length === 0) {
    return (
      <Card>
        <EmptyState title={t('report.noData')} />
      </Card>
    );
  }

  return (
    <Card>
      <Table>
        <THead>
          <TR>
            <TH>{t('collection.chequeDate')}</TH>
            <TH>{t('collection.chequeNo')}</TH>
            <TH>{t('collection.bank')}</TH>
            <TH>{t('collection.customer')}</TH>
            <TH>{t('collection.agent')}</TH>
            <TH align="center">{t('collection.receiptNo')}</TH>
            <TH align="right">{t('collection.amount')}</TH>
          </TR>
        </THead>
        <TBody>
          {data.map((cheque, index) => (
            <TR key={`${cheque.receiptNo}-${cheque.chequeNo}-${index}`}>
              <TD>{formatDate(cheque.chequeDate)}</TD>
              <TD className="font-mono text-xs">{cheque.chequeNo}</TD>
              <TD>{cheque.bankShortCode}</TD>
              <TD>{cheque.shopName}</TD>
              <TD className="text-slate-500">{cheque.agentName}</TD>
              <TD align="center" numeric>
                {cheque.receiptNo}
              </TD>
              <TD align="right" numeric className="font-semibold">
                {formatCents(cheque.amountCents)}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </Card>
  );
}
